import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { OfflineLookupField } from 'frame/components'
import { trimObjectStrings, useDrillDownModalOnClose } from 'utils'
import { lookup } from 'features/drivers/actions'
import { update } from '../../../actions'
import moment from 'moment'
import { hasRoles } from 'acl'
import { useEnabledFeatureToggleList } from 'features/settings'
import { useIsAlfaDownDisableFeatures } from 'features/drivers'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import KeyValue from 'frame/components/mui-textfield'
import ContractDetails from './contract-details'
import StyledModalV2 from 'frame/components/styled-modal-v2'
import { themeV5 } from 'theme/v5'

/* ---------- Component ---------- */

const Contract = ({ data, flags, errorMessage, userRoles, onLookup, onUpdate }) => {
  const featureToggleList = useEnabledFeatureToggleList()
  const disableOutOfHoursFeatureToggle = featureToggleList.includes('OutOfHoursDisabled')
  const isAlfaDownDisableDriverFeatures = useIsAlfaDownDisableFeatures()

  const readOnly = hasRoles(['fleetcoordinator'], userRoles)
  const disabled = disableOutOfHoursFeatureToggle ? false : isAlfaDownDisableDriverFeatures

  const [originalValues, setOriginalValues] = useState(data)
  const [values, setValues] = useState(data)
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)

  const onClose = useDrillDownModalOnClose()

  const onLookupChange = (key) => (option) =>
    setValues({ ...values, [key]: option ? option.value : null })

  const onInputChange = ({ target: { id, value } }) =>
    setValues({ ...values, [id]: value })

  const onSave = () => onUpdate(values)

  useEffect(() => {
    if (flags.update === 'processed' || flags.update === 'error') {
      if (flags.update === 'processed') {
        setOriginalValues(values)
      }
      setOpenConfirmationModal(true)
    }
  }, [flags.update])

  const disabledSave = () =>
    originalValues.costCentre === values.costCentre &&
    originalValues.controller === values.controller &&
    originalValues.location === values.location &&
    originalValues.vehicleNotes === values.vehicleNotes &&
    originalValues.driverNote === values.driverNote

  return (
    <>
      <Card sx={{ boxShadow: 'none', borderRadius: 0 }}>
        <CardContent sx={{ p: 3 }}>
          <ContractDetails
            items={[
              { label: 'Agreement Type', value: data.agreementType },
              { label: 'Term', value: data.term ?? '-' },
              { label: 'Start Date', value: data.startDate ? moment(data.startDate).format('Do MMMM YYYY') : '-' },
              { label: 'Maturity Date', value: data.maturityDate ? moment(data.maturityDate).format('Do MMMM YYYY') : '-' },
              { label: `Contract KM's`, value: data.contractKm ? parseInt(data.contractKm, 10).toLocaleString('en-NZ') : '-' },
              { label: 'Regular Instalment', value: data.regularInstalment ? `$${data.regularInstalment}` : '-' }
            ]}/>

          <Divider sx={{ my: 2, borderColor: themeV5.palette.accent.sky }} />

          <Stack gap="5px" width="50%">
            <OfflineLookupField
              fullWidth
              showAllResult
              allowFreeText
              id="costCentre"
              label="Cost Centre"
              customFleetId={values.thirdPartyNumber}
              lookupPath="costcentre"
              refetchOnChange={values.thirdPartyNumber}
              value={values.costCentre || ''}
              onChange={onLookupChange('costCentre')}
              onLookup={onLookup}
              resourceModel="drivers"/>

            <OfflineLookupField
              fullWidth
              showAllResult
              allowFreeText
              id="controller"
              label="Fleet Controller"
              disabled={disabled}
              customFleetId={values.thirdPartyNumber}
              lookupPath="controller"
              refetchOnChange={values.thirdPartyNumber}
              value={values.controller || ''}
              onChange={onLookupChange('controller')}
              onLookup={onLookup}
              resourceModel="drivers"/>

            <OfflineLookupField
              fullWidth
              id="location"
              label="Location"
              disabled={disabled}
              lookupPath="location"
              value={values.location || 'Unknown'}
              onChange={onLookupChange('location')}
              onLookup={onLookup}
              resourceModel="drivers"/>

            <KeyValue
              multiline
              rows={3}
              id="vehicleNotes"
              label="Vehicle Notes"
              disabled={disabled}
              value={values.vehicleNotes || ''}
              onChange={onInputChange}/>

            <KeyValue
              id="driverNote"
              label="Driver Note"
              disabled={disabled}
              value={values.driverNote || ''}
              onChange={onInputChange}/>
          </Stack>

          <Divider sx={{ mt: 2, mb: 2, borderColor: themeV5.palette.accent.sky }} />

          <Stack direction="row" justifyContent="space-between" gap={1}>
            <Button color="error" variant="outlined" onClick={onClose}>
              Close
            </Button>

            {!readOnly && (
              <Button
                disabled={disabled || disabledSave()}
                variant="contained"
                onClick={onSave}>
                Save Changes
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <StyledModalV2
        open={openConfirmationModal}
        onClose={() => setOpenConfirmationModal(false)}
        status={flags.update === 'processed' ? 'success' : 'error'}
        content={
          <Typography variant="h4" textAlign="center">
            {flags.update === 'processed'
              ? 'Contract details updated successfully.'
              : `Contract details update failed. ${errorMessage}`.trim()}
          </Typography>
        }/>
    </>
  )
}

/* ---------- Wrapper ---------- */

const ContractContainer = (props) => {
  const { id } = useParams()
  return <ConnectedContract {...props} routeId={id} />
}

/* ---------- Redux ---------- */

const mapStateToProps = ({ fleet, settings }, { routeId }) => ({
  data: trimObjectStrings(fleet.data?.[routeId]),
  flags: fleet.flags,
  errorMessage: fleet.errorMessage || '',
  userRoles: settings.roles,
})

const mapDispatchToProps = (dispatch) => ({
  onLookup: (lookupPath, searchKey, customFleetId) =>
    dispatch(lookup(null, {
      resourceId: lookupPath,
      queryParams: { FleetId: customFleetId, Search: searchKey }
    })),
  onUpdate: (payload) => dispatch(update(payload)),
})

const ConnectedContract = connect(
  mapStateToProps,
  mapDispatchToProps
)(Contract)

export default ContractContainer
